import React, { forwardRef, useEffect, useState } from "react";
import { bool, func, node, object, oneOf, string } from "prop-types";

import componentSizes from "../../constants/componentSizes";
import CountryCodeSelect from "./components/CountryCodeSelect";
import PhoneNumberInputBase from "./components/PhoneNumberInput";
import getSuffix from "./utilties/getSuffix";
import parsePhoneNumber from "./utilties/parsePhoneNumber";
import removeSuffix from "./utilties/removeSuffix";
import {
  DEFAULT_COUNTRY,
  DEFAULT_COUNTRY_CODE,
  PHONE_NUMBER_FIELD_SIZES
} from "./constants";
import * as S from "./styled";

// eslint-disable-next-line react/prop-types
const RenderProps = props => <>{props.children(props)}</>;

export const PHONE_NUMBER_FIELD_PSEUDO_EVENT_TYPE = "PhoneFieldChange";

const PhoneField = forwardRef(
  (
    {
      className,
      countryCodeSelectOptions,
      defaultValue,
      disabled,
      error,
      hint,
      id,
      isValid,
      label,
      name,
      onChange,
      phoneNumberInputOptions,
      required,
      size,
      value,
      ...passThroughProps
    },
    ref
  ) => {
    const phoneNumberInputId = id;
    /**
     * The countrySelectTouched and phoneNumberInputTouched state fields are used to
     * prevent onChange events from firing until the user touches either the country
     * select input or the phone number input.
     */
    const [countrySelectTouched, setCountrySelectTouched] = useState(false);
    const [phoneNumberInputTouched, setPhoneNumberInputTouched] = useState(
      false
    );

    const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
    const [country, setCountry] = useState(DEFAULT_COUNTRY);
    const [phoneNumberVal, setPhoneNumberVal] = useState("");

    const handleCountrySelectChange = (
      _,
      nextCountry,
      { countryCallingCode }
    ) => {
      setCountrySelectTouched(true);
      setCountry(nextCountry);
      setCountryCode(countryCallingCode);
    };

    const handlePhoneNumberInputChange = (_, nextPhoneNumberVal) => {
      setPhoneNumberInputTouched(true);
      setPhoneNumberVal(nextPhoneNumberVal);
    };

    const handleValueChange = () => {
      const valueToParse = value || defaultValue;

      const {
        country: _country,
        countryCode: _countryCode,
        phoneNumber: _phoneNumberVal,
        suffix: _suffix
      } = parsePhoneNumber(valueToParse);

      const nextPhoneNumberVal = `${_phoneNumberVal}${_suffix}`;

      setCountry(_country);
      setCountryCode(_countryCode);
      setPhoneNumberVal(nextPhoneNumberVal);
    };

    const createPseudoEvent = ({ targetName = name, targetValue }) => ({
      type: PHONE_NUMBER_FIELD_PSEUDO_EVENT_TYPE,
      target: {
        name: targetName,
        value: targetValue
      }
    });

    const handlePhoneNumberChange = () => {
      /**
       * Do not fire onChange events unless the user has touched one of the
       * inputs. Prevents onChange from firing when the component mounts.
       */
      if (!countrySelectTouched && !phoneNumberInputTouched) return;

      const suffix = getSuffix(phoneNumberVal);
      const phoneNumber = removeSuffix(phoneNumberVal);

      const nextCompletePhoneNumberVal = `+${countryCode}${phoneNumberVal}`;

      const pseudoEvent = createPseudoEvent({
        targetValue: nextCompletePhoneNumberVal
      });

      onChange(pseudoEvent, nextCompletePhoneNumberVal, {
        country,
        countryCode,
        phoneNumber,
        suffix
      });
    };

    /**
     * If PhoneField is uncontrolled, this event should fire once
     * when the component mounts. The defaultValue prop will be used to
     * set initial values for the field.
     *
     * If PhoneField is controlled, this event will fire every time the
     * value prop changes. We assume that either a defaultValue or value prop
     * will be passed.
     */
    useEffect(handleValueChange, [value]);

    useEffect(handlePhoneNumberChange, [country, phoneNumberVal]);

    return (
      <S.Field
        ref={ref}
        className={className}
        error={error}
        helper={country === DEFAULT_COUNTRY ? "(XXX) XXX-XXXX" : null}
        hint={hint}
        id={phoneNumberInputId}
        isValid={isValid}
        label={label}
        {...passThroughProps}
      >
        <RenderProps>
          {({ isValid: isInputValid, ...fieldPassThroughProps }) => (
            <S.FieldContent {...passThroughProps}>
              <S.CountryCodeSelectorLabel htmlFor="country_code_selector">
                Country Code
              </S.CountryCodeSelectorLabel>
              <CountryCodeSelect
                aria-describedby={fieldPassThroughProps["aria-labelledby"]}
                disabled={disabled}
                id="country_code_selector"
                onChange={handleCountrySelectChange}
                size={size}
                value={country}
                {...countryCodeSelectOptions}
              />
              <PhoneNumberInputBase
                aria-describedby={fieldPassThroughProps["aria-describedby"]}
                aria-labelledby={fieldPassThroughProps["aria-labelledby"]}
                country={country}
                disabled={disabled}
                id={phoneNumberInputId}
                isValid={isInputValid}
                onChange={handlePhoneNumberInputChange}
                required={required}
                size={size}
                value={phoneNumberVal}
                {...phoneNumberInputOptions}
              />
            </S.FieldContent>
          )}
        </RenderProps>
      </S.Field>
    );
  }
);

PhoneField.displayName = "PhoneField";

PhoneField.propTypes = {
  className: string,
  /**
   * Props passed through to the country code select component. This
   * is the prop we would use to pass a data-qa or other HTML attributes.
   */
  // eslint-disable-next-line react/forbid-prop-types
  countryCodeSelectOptions: object,
  /**
   * The initial value of PhoneField. When the number is prefixed with
   * a "+" symbol, PhoneField will attempt to parse the number as an
   * international phone number. If value passed to this prop does not have a "+,"
   * PhoneField will assume the phone number is US-based.
   */
  defaultValue: string,
  /**
   * When true, applies HTML disabled attribute to underlying
   * inputs. Styling conveying that these inputs are disabled is automatically
   * applied.
   */
  disabled: bool,
  /**
   * An error message that will be rendered beneath the component when the
   * isValid prop is set to false.
   */
  error: node,
  /**
   * Text appearing to the right of the component's label. By convention, hint
   * is set to either "optional" or "required."
   */
  hint: string.isRequired,
  /**
   * Used by `Field` internally to generate HTML attributes that support accessibility
   */
  id: string.isRequired,
  /**
   * Boolean value capturing the validity of the value present in PhoneField.
   * When false, an error message will display if the "error" prop has been
   * configured.
   */
  isValid: bool,
  /**
   * The label applied to the phone number input part of PhoneField. This
   * label will also be used to describe the country code selector part of this
   * organism.
   */
  label: node,
  /**
   * Adds a name property to the pseudo-event returned as the first argument
   * to the function passed through the onChange prop.
   */
  name: string,
  onChange: func,
  /**
   * A function called with the following arguments:
   *
   * Pseudo Event (object) - a pseudo event object containing the following fields
   * - type: PHONE_NUMBER_FIELD_PSEUDO_EVENT_TYPE
   * - target (object) - a pseudo EventTarget
   *      - name (string) - defaults to "" or can be provided through the "name" props
   *      - value (string) - a complete phone number in the format +[country_calling_code][phone_number][suffix]
   * Value (string) - a complete phone number in the format +[country_calling_code][phone_number][suffix]
   * Data (object) - an object containing the following fields:
   * - country (string) - a two letter country code, e.g. "US"
   * - countryCode (string) - the phone number's country calling code, e.g. "1"
   * - phoneNumber (string) - the phone number entered by the user, without country calling code or suffix
   * - suffix (string) - any suffix added to the phone number. This field is used to capture extensions if entered by the user, e.g. ("x123")
   */
  /**
   * Props passed through to the phone number input component. This
   * is the prop we would use to pass a data-qa or other HTML attributes.
   */
  // eslint-disable-next-line react/forbid-prop-types
  phoneNumberInputOptions: object,
  /**
   * When true, applies HTML required attribute to phone number "tel" input
   */
  required: bool,
  size: oneOf(PHONE_NUMBER_FIELD_SIZES),
  value: string
};

PhoneField.defaultProps = {
  className: "",
  countryCodeSelectOptions: {},
  defaultValue: "",
  disabled: false,
  error: "",
  label: "Phone Number",
  name: "",
  onChange: () => {},
  isValid: true,
  phoneNumberInputOptions: {},
  required: false,
  size: componentSizes.MD,
  value: ""
};

export default PhoneField;
